import React, { useState, useEffect, useCallback } from 'react';
import { HeroSection } from './Hero';
import { SearchBar, LocationPicker } from './Search';
import { CategoriesGrid, CategoryCard } from './Category';
import { ListingsGrid } from './Listing';
import { FilterIcon } from './Icons';
import { ConversationItem } from './Conversation';
import { MessageBubble, ChatInput } from './Message';
import { MobileHeader } from './Header';
import { CreateAdForm } from './CreateAdForm';
import { ListingsGridSkeleton, EmptyStatePlaceholder } from './Placeholders';
import { ImageUploader } from './CloudinaryUploader';
import { apiService, useAuth } from '../auth';
import { mockConversations, mockMessages } from './MockData';

// Home Screen Component
export const HomeScreen = ({ onCategoryClick, onListingContact, onViewDetails, isGuest = false, onRequireAuth }) => {
  const [showCreateAdForm, setShowCreateAdForm] = useState(false);
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Charger les données depuis l'API
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Charger les catégories et les annonces en parallèle
        const [categoriesData, listingsData] = await Promise.all([
          apiService.getCategories(),
          apiService.getListings({ page: 1, limit: 10, sort_by: 'created_at', sort_order: 'desc' })
        ]);
        
        // Calculer le nombre d'annonces par catégorie
        const categoryCountMap = {};
        (listingsData.items || []).forEach(listing => {
          if (listing.category_id) {
            categoryCountMap[listing.category_id] = (categoryCountMap[listing.category_id] || 0) + 1;
          }
        });
        
        // Enrichir les catégories avec le nombre d'annonces
        const enrichedCategories = categoriesData.map(category => ({
          ...category,
          count: categoryCountMap[category.id] || 0
        }));
        
        setCategories(enrichedCategories);
        
        // Enrichir les annonces avec les informations de catégorie
        const enrichedListings = (listingsData.items || []).map(listing => {
          const category = categoriesData.find(cat => cat.id === listing.category_id);
          return {
            ...listing,
            category: category || null,
            categoryName: category?.name || 'Non catégorisé',
            categoryIcon: category?.icon || '📦'
          };
        });
        
        setListings(enrichedListings);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleLike = async (listingId, isLiked) => {
    // Mettre à jour l'état local immédiatement pour une meilleure UX
    setListings(prevListings => 
      prevListings.map(listing => 
        listing.id === listingId 
          ? { ...listing, is_favorited: isLiked }
          : listing
      )
    );
  };

  // Fonction pour filtrer par catégorie
  const handleCategoryFilter = useCallback(async (category) => {
    console.log('Filtrage par catégorie:', category.name);
    setLoading(true);
    
    try {
      // Charger les annonces filtrées par catégorie
      const listingsData = await apiService.getListings({ 
        category_id: category.id,
        page: 1, 
        limit: 20, 
        sort_by: 'created_at', 
        sort_order: 'desc' 
      });
      
      // Enrichir les annonces avec les informations de catégorie
      const enrichedListings = (listingsData.items || []).map(listing => {
        const listingCategory = categories.find(cat => cat.id === listing.category_id);
        return {
          ...listing,
          category: listingCategory || null,
          categoryName: listingCategory?.name || 'Non catégorisé',
          categoryIcon: listingCategory?.icon || '📦'
        };
      });
      
      setListings(enrichedListings);
      
    } catch (error) {
      console.error('Erreur lors du filtrage par catégorie:', error);
    } finally {
      setLoading(false);
    }
  }, [categories]);

  const handleCreateAd = () => {
    if (isGuest) {
      onRequireAuth && onRequireAuth();
    } else {
      setShowCreateAdForm(true);
    }
  };

  const handleCloseCreateAd = () => {
    setShowCreateAdForm(false);
  };

  // Fonctions de recherche pour les différents écrans
  const handleHomeSearch = async (searchTerm) => {
    console.log('Recherche sur la page d\'accueil:', searchTerm);
    
    // Si le terme de recherche est vide, recharger toutes les annonces
    if (!searchTerm.trim()) {
      try {
        setLoading(true);
        const listingsData = await apiService.getListings();
        
        // Enrichir les annonces avec les informations de catégorie
        const enrichedListings = (listingsData.items || []).map(listing => {
          const category = categories.find(cat => cat.id === listing.category_id);
          return {
            ...listing,
            category: category || null,
            categoryName: category?.name || 'Non catégorisé',
            categoryIcon: category?.icon || '📦'
          };
        });
        
        setListings(enrichedListings);
      } catch (error) {
        console.error('Erreur lors du rechargement des annonces:', error);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      
      // D'abord essayer la recherche via l'API
      const result = await apiService.searchListings(searchTerm, { limit: 20 });
      if (result.success && result.data) {
        // Enrichir les résultats avec les informations de catégorie
        const enrichedResults = result.data.map(listing => {
          const category = categories.find(cat => cat.id === listing.category_id);
          return {
            ...listing,
            category: category || null,
            categoryName: category?.name || 'Non catégorisé',
            categoryIcon: category?.icon || '📦'
          };
        });
        
        setListings(enrichedResults);
      } else {
        // Fallback : recherche locale dans les annonces existantes
        const queryLower = searchTerm.toLowerCase();
        const filteredListings = listings.filter(listing => {
          const title = listing.title?.toLowerCase() || '';
          const description = listing.description?.toLowerCase() || '';
          const location = listing.location?.toLowerCase() || '';
          const categoryName = listing.categoryName?.toLowerCase() || '';
          
          return title.includes(queryLower) || 
                 description.includes(queryLower) || 
                 location.includes(queryLower) ||
                 categoryName.includes(queryLower);
        });
        
        setListings(filteredListings);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      
      // En cas d'erreur, faire une recherche locale
      const queryLower = searchTerm.toLowerCase();
      const filteredListings = listings.filter(listing => {
        const title = listing.title?.toLowerCase() || '';
        const description = listing.description?.toLowerCase() || '';
        const location = listing.location?.toLowerCase() || '';
        const categoryName = listing.categoryName?.toLowerCase() || '';
        
        return title.includes(queryLower) || 
               description.includes(queryLower) || 
               location.includes(queryLower) ||
               categoryName.includes(queryLower);
      });
      
      setListings(filteredListings);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySearch = (searchTerm) => {
    console.log('Recherche dans les catégories:', searchTerm);
    // TODO: Implémenter la logique de recherche des catégories
    // setCategories(await apiService.searchCategories(searchTerm));
  };

  const handleConversationSearch = (searchTerm) => {
    console.log('Recherche dans les conversations:', searchTerm);
    // TODO: Implémenter la logique de recherche des conversations
    // setConversations(await apiService.searchConversations(searchTerm));
  };

  const handleCreateAdSuccess = useCallback((newListing) => {
    console.log('Nouvelle annonce créée:', newListing);
    
    // Enrichir la nouvelle annonce avec les informations de catégorie
    const category = categories.find(cat => cat.id === newListing.category_id);
    const enrichedListing = {
      ...newListing,
      category: category || null,
      categoryName: category?.name || 'Non catégorisé',
      categoryIcon: category?.icon || '📦'
    };
    
    // Ajouter la nouvelle annonce à la liste
    setListings(prevListings => [enrichedListing, ...prevListings]);
    
    // Mettre à jour le compte de la catégorie correspondante de façon optimisée
    if (newListing.category_id) {
      setCategories(prevCategories => 
        prevCategories.map(cat => 
          cat.id === newListing.category_id 
            ? { ...cat, count: (cat.count || 0) + 1 }
            : cat
        )
      );
    }
    
    setShowCreateAdForm(false);
    
    // Optionnel: Recharger les données pour s'assurer de la cohérence
    // loadData();
  }, [categories]);

  // Fonction pour réinitialiser les filtres et afficher toutes les annonces
  const handleShowAllListings = async () => {
    setLoading(true);
    try {
      const listingsData = await apiService.getListings({ 
        page: 1, 
        limit: 20, 
        sort_by: 'created_at', 
        sort_order: 'desc' 
      });
      
      // Calculer le nombre d'annonces par catégorie
      const categoryCountMap = {};
      (listingsData.items || []).forEach(listing => {
        if (listing.category_id) {
          categoryCountMap[listing.category_id] = (categoryCountMap[listing.category_id] || 0) + 1;
        }
      });
      
      // Mettre à jour les catégories avec les nouveaux comptes
      setCategories(prevCategories => 
        prevCategories.map(category => ({
          ...category,
          count: categoryCountMap[category.id] || 0
        }))
      );
      
      // Enrichir les annonces avec les informations de catégorie
      const enrichedListings = (listingsData.items || []).map(listing => {
        const category = categories.find(cat => cat.id === listing.category_id);
        return {
          ...listing,
          category: category || null,
          categoryName: category?.name || 'Non catégorisé',
          categoryIcon: category?.icon || '📦'
        };
      });
      
      setListings(enrichedListings);
    } catch (error) {
      console.error('Erreur lors du rechargement des annonces:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pb-20 bg-gray-50 min-h-screen">
        <HeroSection onCreateAd={handleCreateAd} />
        
        <div className="p-4">
          <div className="h-12 bg-gray-200 rounded-lg animate-pulse mb-3"></div>
          <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
        
        <div className="p-4">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4 animate-pulse"></div>
          <div className="grid grid-cols-4 gap-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="bg-gray-200 rounded-lg h-20 animate-pulse"></div>
            ))}
          </div>
        </div>
        
        <ListingsGridSkeleton count={6} />
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen">
      <HeroSection onCreateAd={handleCreateAd} />
      
      {/* Bannière d'invitation pour les invités */}
      {isGuest && (
        <div className="mx-4 mb-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">Découvrez LaTeranga !</h3>
              <p className="text-blue-100 text-sm">
                Créez un compte pour contacter les vendeurs, créer vos annonces et bien plus...
              </p>
            </div>
            <button
              onClick={onRequireAuth}
              className="ml-3 bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors text-sm"
            >
              Rejoindre
            </button>
          </div>
        </div>
      )}
      
      <div className="p-4">
        <SearchBar 
          placeholder="Que cherchez-vous ?" 
          onSearch={handleHomeSearch} 
          enableAutocomplete={true}
          existingListings={listings}
        />
        <div className="mt-3">
          <LocationPicker />
        </div>
      </div>
      
      <CategoriesGrid 
        categories={categories} 
        onCategoryClick={handleCategoryFilter}
        listings={listings}
        onViewDetails={onViewDetails}
      />
      
      {/* Bouton pour afficher toutes les annonces */}
      <div className="px-4 pb-2">
        <button
          onClick={handleShowAllListings}
          className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
        >
          📋 Voir toutes les annonces
        </button>
      </div>
      
      <ListingsGrid 
        listings={listings}
        onLike={handleLike}
        onContact={onListingContact}
        onViewDetails={onViewDetails}
      />

      {/* Modal de création d'annonce */}
      {showCreateAdForm && (
        <CreateAdForm
          onClose={handleCloseCreateAd}
          onSuccess={handleCreateAdSuccess}
        />
      )}
    </div>
  );
};

// Categories Screen Component avec filtrage
export const CategoriesScreen = ({ onCategoryClick, onViewDetails }) => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        // Charger les catégories et toutes les annonces pour calculer les comptes
        const [categoriesData, listingsData] = await Promise.all([
          apiService.getCategories(),
          apiService.getListings({ page: 1, limit: 100 }) // Charger plus d'annonces pour avoir des comptes plus précis
        ]);
        
        // Calculer le nombre d'annonces par catégorie
        const categoryCountMap = {};
        (listingsData.items || []).forEach(listing => {
          if (listing.category_id) {
            categoryCountMap[listing.category_id] = (categoryCountMap[listing.category_id] || 0) + 1;
          }
        });
        
        // Enrichir les catégories avec le nombre d'annonces
        const enrichedCategories = categoriesData.map(category => ({
          ...category,
          count: categoryCountMap[category.id] || 0
        }));
        
        // Enrichir les annonces avec les informations de catégorie
        const enrichedListings = (listingsData.items || []).map(listing => {
          const category = categoriesData.find(cat => cat.id === listing.category_id);
          return {
            ...listing,
            category: category || null,
            categoryName: category?.name || 'Non catégorisé',
            categoryIcon: category?.icon || '📦'
          };
        });
        
        setCategories(enrichedCategories);
        setFilteredCategories(enrichedCategories);
        setListings(enrichedListings);
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Fonction de recherche dans les catégories
  const handleCategorySearch = (searchTerm) => {
    console.log('Recherche dans les catégories:', searchTerm);
    
    if (!searchTerm.trim()) {
      setFilteredCategories(categories);
      return;
    }
    
    const queryLower = searchTerm.toLowerCase();
    const filtered = categories.filter(category => 
      category.name?.toLowerCase().includes(queryLower) ||
      category.description?.toLowerCase().includes(queryLower)
    );
    
    setFilteredCategories(filtered);
  };

  if (loading) {
    return (
      <div className="pb-20 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des catégories...</p>
        </div>
      </div>
    );
  }

    return (
      <div className="pb-20 bg-gray-50 min-h-screen">
        <div className="p-4">
          <SearchBar placeholder="Rechercher des catégories..." onSearch={handleCategorySearch} />
          <div className="mt-4">
            <FilterIcon />
          </div>
        </div>      <div className="px-4">
        <div className="grid grid-cols-2 gap-3">
          {filteredCategories.map(category => (
            <CategoryCard 
              key={category.id} 
              category={category} 
              onClick={onCategoryClick}
              listings={listings}
              onViewDetails={onViewDetails}
              enableDropdown={true}
            />
          ))}
        </div>
        
        {filteredCategories.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500">Aucune catégorie trouvée</p>
            <p className="text-gray-400 text-sm mt-1">Essayez avec d'autres mots-clés</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Messages Screen Component
export const MessagesScreen = ({ onConversationClick }) => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const conversationsData = await apiService.getConversations();
      setConversations(conversationsData);
      setFilteredConversations(conversationsData);
    } catch (error) {
      console.error('Erreur lors du chargement des conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Fonction de recherche dans les conversations
  const handleConversationSearch = useCallback((searchTerm) => {
    console.log('Recherche dans les conversations:', searchTerm);
    
    if (!searchTerm.trim()) {
      setFilteredConversations(conversations);
      return;
    }
    
    const queryLower = searchTerm.toLowerCase();
    const filtered = conversations.filter(conversation => {
      // Rechercher dans le nom des participants
      const participantNames = conversation.participants_info?.map(p => 
        `${p.first_name} ${p.last_name}`.toLowerCase()
      ).join(' ') || '';
      
      // Rechercher dans le titre de l'annonce associée
      const listingTitle = conversation.listing?.title?.toLowerCase() || '';
      
      // Rechercher dans le dernier message
      const lastMessage = conversation.last_message?.content?.toLowerCase() || '';
      
      return participantNames.includes(queryLower) ||
             listingTitle.includes(queryLower) ||
             lastMessage.includes(queryLower);
    });
    
    setFilteredConversations(filtered);
  }, [conversations]);

  if (loading) {
    return (
      <div className="pb-20 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-white min-h-screen">
      <div className="p-4 border-b border-gray-100">
        <SearchBar placeholder="Rechercher dans les conversations..." onSearch={handleConversationSearch} />
      </div>
      
      <div>
        {filteredConversations.length > 0 ? (
          filteredConversations.map(conversation => (
            <ConversationItem 
              key={conversation.id}
              conversation={conversation}
              onClick={onConversationClick}
            />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.418 8-9.899 8a9.86 9.86 0 01-5.626-1.725L3 21l1.699-3.475A7.946 7.946 0 013 12c0-4.418 4.418-8 9.899-8C16.581 4 21 7.582 21 12z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {conversations.length === 0 ? 'Aucune conversation' : 'Aucun résultat trouvé'}
            </h3>
            <p className="text-gray-500 text-center">
              {conversations.length === 0 
                ? 'Vos conversations apparaîtront ici. Contactez un vendeur pour commencer à discuter.'
                : 'Essayez avec d\'autres mots-clés pour affiner votre recherche.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Chat Screen Component
export const ChatScreen = ({ conversation, onBack, onSend }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const loadMessages = async () => {
      if (!conversation) return;
      
      setLoading(true);
      try {
        const messagesData = await apiService.getConversationMessages(conversation.id);
        setMessages(messagesData);
        
        // Si c'est une nouvelle conversation (pas de messages), envoyer un message de bienvenue automatique
        if (messagesData.length === 0 && conversation.listing) {
          const defaultMessage = `Bonjour je suis intéressé par votre annonce. Votre produit est-il toujours disponible ?`;

          try {
            console.log('📨 Envoi du message de bienvenue automatique...');
            const messageResult = await apiService.sendMessage(conversation.id, defaultMessage);
            if (messageResult.success) {
              console.log('✅ Message de bienvenue envoyé');
              setMessages([messageResult.data]);
            }
          } catch (messageError) {
            console.error('❌ Erreur lors de l\'envoi du message de bienvenue:', messageError);
          }
        }
        
        // Marquer la conversation comme lue
        await apiService.markConversationRead(conversation.id);
      } catch (error) {
        console.error('Erreur lors du chargement des messages:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, [conversation]);

  const handleSendMessage = async (content) => {
    if (!conversation || !content.trim()) return;
    
    try {
      const result = await apiService.sendMessage(conversation.id, content);
      if (result.success) {
        setMessages(prev => [...prev, result.data]);
        onSend && onSend(content);
      } else {
        alert('Erreur lors de l\'envoi du message: ' + result.message);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Erreur lors de l\'envoi du message');
    }
  };

  const handleSendImage = async (imageUrl) => {
    if (!conversation || !imageUrl) return;
    
    try {
      const result = await apiService.sendMessage(conversation.id, imageUrl, 'image');
      if (result.success) {
        setMessages(prev => [...prev, result.data]);
        onSend && onSend(imageUrl);
      } else {
        alert('Erreur lors de l\'envoi de l\'image: ' + result.message);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'image:', error);
      alert('Erreur lors de l\'envoi de l\'image');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const result = await apiService.deleteMessage(messageId);
      if (result.success) {
        // Supprimer le message de l'état local
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
      } else {
        alert('Erreur lors de la suppression: ' + result.message);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du message');
    }
  };

  const getOtherParticipant = () => {
    if (!conversation || !conversation.participants_info) return null;
    return conversation.participants_info.find(p => p.id !== user?.id);
  };

  const formatMessageForDisplay = (message) => {
    const baseMessage = {
      id: message.id,
      timestamp: new Date(message.created_at).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      isOwn: message.sender_id === user?.id,
      sender: message.sender
    };

    // Pour les images, mettre l'URL dans imageUrl
    if (message.message_type === 'image') {
      return {
        ...baseMessage,
        imageUrl: message.content
      };
    }
    
    // Pour le texte, mettre le contenu dans message
    return {
      ...baseMessage,
      message: message.content
    };
  };

  const otherParticipant = getOtherParticipant();

  return (
    <div className="flex flex-col h-screen">
      <MobileHeader 
        title={otherParticipant ? `${otherParticipant.first_name} ${otherParticipant.last_name}` : 'Chat'}
        onBack={onBack}
        rightAction={
          <button className="p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zM12 13a1 1 0 110-2 1 1 0 010 2zM12 20a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        }
      />
      
      {conversation?.listing && (
        <div className="bg-blue-50 p-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {/* Afficher l'image seulement si elle existe */}
            {(conversation.listing.images?.[0]?.secure_url || conversation.listing.images?.[0]?.url) && (
              <img 
                src={conversation.listing.images[0].secure_url || conversation.listing.images[0].url}
                alt="Listing"
                className="w-10 h-10 rounded object-cover"
                onError={(e) => {
                  // Cacher l'image en cas d'erreur de chargement
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div>
              <p className="text-sm font-medium text-gray-900">{conversation.listing.title}</p>
              <p className="text-xs text-gray-600">€{conversation.listing.price}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : messages.length > 0 ? (
          messages.map(message => (
            <MessageBubble 
              key={message.id} 
              message={formatMessageForDisplay(message)} 
              onDelete={handleDeleteMessage}
            />
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Aucun message pour le moment</p>
            <p className="text-gray-400 text-sm mt-1">Envoyez le premier message !</p>
          </div>
        )}
      </div>
      
      <ChatInput conversationId={conversation.id} onSend={handleSendMessage} onSendImage={handleSendImage} />
    </div>
  );
};

// Profile Screen Component
export const ProfileScreen = ({ onLogout, onCreateAd }) => {
  const { user } = useAuth();
  const [userStats, setUserStats] = useState({
    listingsCount: 0,
    favoritesCount: 0,
    rating: 5.0,
    reviewsCount: 0
  });
  const [userListings, setUserListings] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(true);
  const [showEditProfile, setShowEditProfile] = useState(false);

  // Charger les données utilisateur
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // Charger les statistiques utilisateur
        const stats = await apiService.getUserStats(user.id);
        setUserStats(stats);

        // Charger les annonces de l'utilisateur
        const listings = await apiService.getUserListings(user.id);
        setUserListings(listings);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  const formatMemberSince = (dateString) => {
    const date = new Date(dateString);
    return `Membre depuis ${date.getFullYear()}`;
  };

  const handleEditProfile = () => {
    setShowEditProfile(true);
  };

  const handleDeleteListing = (listingId) => {
    setUserListings(prevListings => 
      prevListings.filter(listing => listing.id !== listingId)
    );
    // Mettre à jour les statistiques
    setUserStats(prev => ({
      ...prev,
      listingsCount: prev.listingsCount - 1
    }));
  };

  const [editingListing, setEditingListing] = useState(null);

  const handleEditListing = (listing) => {
    setEditingListing(listing);
  };

  const handleEditComplete = (updatedListing) => {
    setUserListings(prevListings => 
      prevListings.map(listing => 
        listing.id === updatedListing.id ? updatedListing : listing
      )
    );
    setEditingListing(null);
  };

  const handleLogoutClick = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      onLogout();
    }
  };

  // ...existing code...

  if (!user) {
    return (
      <div className="pb-20 bg-gray-50 min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Veuillez vous connecter pour accéder à votre profil.</p>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen">
      {/* En-tête du profil */}
      <div className="bg-white border-b border-gray-100">
        <div className="p-6 text-center">
          <div className="relative inline-block">
            <img 
              src={user.avatar_url || 'https://ui-avatars.com/api/?name=' + user.first_name + '+' + user.last_name}
              alt="Profil"
              className="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-4 border-gray-100"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/80x80?text=Utilisateur';
              }}
            />
            <button 
              onClick={handleEditProfile}
              className="absolute bottom-4 right-0 bg-blue-600 text-white rounded-full p-1 text-xs hover:bg-blue-700"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
          
          <h2 className="text-xl font-bold text-gray-900">
            {user.first_name} {user.last_name}
          </h2>
          <p className="text-gray-600">{user.email}</p>
          <p className="text-sm text-gray-500 mt-1">
            {formatMemberSince(user.created_at)}
          </p>
          
          {/* Statistiques */}
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="text-center">
              <div className="font-semibold text-gray-900">{userStats.listingsCount}</div>
              <div className="text-gray-500">Annonces</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-900">{userStats.favoritesCount}</div>
              <div className="text-gray-500">Favoris</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-900">{userStats.rating}⭐</div>
              <div className="text-gray-500">({userStats.reviewsCount} avis)</div>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="flex border-t border-gray-100">
          <button
            onClick={() => setActiveTab('info')}
            className={`flex-1 py-3 text-center text-sm font-medium ${
              activeTab === 'info' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500'
            }`}
          >
            Informations
          </button>
          <button
            onClick={() => setActiveTab('listings')}
            className={`flex-1 py-3 text-center text-sm font-medium ${
              activeTab === 'listings' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500'
            }`}
          >
            Mes Annonces
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 py-3 text-center text-sm font-medium ${
              activeTab === 'settings' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500'
            }`}
          >
            Paramètres
          </button>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="p-4">
        {activeTab === 'info' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Informations personnelles</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Nom complet</span>
                  <span className="text-gray-900">{user.first_name} {user.last_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Email</span>
                  <span className="text-gray-900">{user.email}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Téléphone</span>
                  <span className="text-gray-900">{user.phone || 'Non renseigné'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Localisation</span>
                  <span className="text-gray-900">{user.location || 'Non renseignée'}</span>
                </div>
              </div>
              <button 
                onClick={handleEditProfile}
                className="w-full mt-4 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                Modifier le profil
              </button>
            </div>
          </div>
        )}

        {activeTab === 'listings' && (
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Chargement...</p>
              </div>
            ) : userListings.length > 0 ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Mes annonces ({userListings.length})</h3>
                  <select 
                    className="text-sm border border-gray-300 rounded-lg px-3 py-2"
                    onChange={(e) => {
                      // TODO: Filtrer par statut
                      console.log('Filtre:', e.target.value);
                    }}
                  >
                    <option value="all">Toutes</option>
                    <option value="active">Actives</option>
                    <option value="sold">Vendues</option>
                    <option value="inactive">Inactives</option>
                  </select>
                </div>
                <div className="flex flex-wrap">
                  {userListings.map(listing => (
                    <UserListingCard
                      key={listing.id}
                      listing={listing}
                      onDelete={handleDeleteListing}
                      onEdit={handleEditListing}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-8 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 002-2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune annonce</h3>
                <p className="text-gray-600 mb-4">Vous n'avez pas encore publié d'annonces.</p>
                <button 
                  onClick={onCreateAd}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                >
                  Créer une annonce
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Compte</h3>
              <div className="space-y-3">
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-between">
                  <span>Notifications</span>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-between">
                  <span>Confidentialité</span>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-between">
                  <span>Aide et support</span>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Actions</h3>
              <div className="space-y-3">
                <button 
                  onClick={handleLogoutClick}
                  className="w-full text-left p-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                >
                  Se déconnecter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'édition du profil */}
      {showEditProfile && (
        <EditProfileModal 
          user={user}
          onClose={() => setShowEditProfile(false)}
          onUpdate={(updatedUser) => {
            // Mettre à jour l'utilisateur dans le contexte
            setShowEditProfile(false);
          }}
        />
      )}

      {/* Modal d'édition d'annonce */}
      {editingListing && (
        <EditListingModal 
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onUpdate={handleEditComplete}
        />
      )}
    </div>
  );
};

// Modal d'édition du profil
const EditProfileModal = ({ user, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    phone: user.phone || '',
    location: user.location || ''
  });
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user.avatar_url);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Mettre à jour le profil
      const updatedUser = await apiService.updateProfile(formData);
      
      // Upload de l'avatar si nécessaire
      if (avatarFile) {
        const avatarFormData = new FormData();
        avatarFormData.append('file', avatarFile);
        
        const avatarResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/avatar`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: avatarFormData
        });
        
        if (avatarResponse.ok) {
          const avatarData = await avatarResponse.json();
          updatedUser.avatar_url = avatarData.original;
        }
      }
      
      onUpdate(updatedUser);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Modifier le profil</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Avatar */}
            <div className="text-center">
              <div className="relative inline-block">
                <img
                  src={avatarPreview || 'https://via.placeholder.com/80x80?text=Utilisateur'}
                  alt="Avatar"
                  className="w-20 h-20 rounded-full object-cover border-4 border-gray-100"
                />
                <label className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-1 cursor-pointer hover:bg-blue-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 002-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500 mt-2">Cliquez pour changer la photo</p>
            </div>

            {/* Prénom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Téléphone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Votre numéro de téléphone"
              />
            </div>

            {/* Localisation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Localisation
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Votre ville ou région"
              />
            </div>

            {/* Boutons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Modal d'édition d'annonce
const EditListingModal = ({ listing, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: listing.title,
    description: listing.description || '',
    price: listing.price,
    category_id: listing.category_id,
    location: listing.location || '',
    condition: listing.condition || 'new',
    status: listing.status || 'active'
  });
  const [images, setImages] = useState(listing.images || []);
  const [newImages, setNewImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);

  // Charger les catégories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await apiService.getCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
      }
    };
    loadCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (files) => {
    if (files.length + images.length + newImages.length > 5) {
      alert('Vous ne pouvez pas ajouter plus de 5 images au total');
      return;
    }

    const uploadedImages = [];
    for (const file of files) {
      try {
        const formDataImage = new FormData();
        formDataImage.append('file', file);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formDataImage
        });

        if (response.ok) {
          const imageData = await response.json();
          uploadedImages.push(imageData);
        }
      } catch (error) {
        console.error('Erreur lors de l\'upload:', error);
      }
    }
    
    setNewImages(prev => [...prev, ...uploadedImages]);
  };

  const removeExistingImage = (index) => {
    const imageToRemove = images[index];
    setRemovedImages(prev => [...prev, imageToRemove]);
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const getImageUrl = (image) => {
    if (typeof image === 'string') {
      return image;
    } else if (image && typeof image === 'object') {
      return image.secure_url || image.url || image.original || '';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Préparer les données de mise à jour
      const updateData = {
        ...formData,
        images: [...images, ...newImages],
        removed_images: removedImages
      };

      const result = await apiService.updateListing(listing.id, updateData);
      
      if (result.success) {
        onUpdate(result.data);
        alert('Annonce mise à jour avec succès');
      } else {
        alert('Erreur lors de la mise à jour: ' + result.message);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour de l\'annonce');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Modifier l'annonce</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre de l'annonce
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Décrivez votre article..."
            />
          </div>

          {/* Prix et Catégorie */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prix (€)
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catégorie
              </label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Sélectionner une catégorie</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Localisation et État */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localisation
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ville, région..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                État
              </label>
              <select
                name="condition"
                value={formData.condition}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="new">Neuf</option>
                <option value="like_new">Comme neuf</option>
                <option value="good">Bon état</option>
                <option value="fair">État correct</option>
                <option value="poor">Mauvais état</option>
              </select>
            </div>
          </div>

          {/* Statut */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut de l'annonce
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="sold">Vendue</option>
              <option value="pending">En attente</option>
            </select>
          </div>

          {/* Images existantes */}
          {images.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images actuelles
              </label>
              <div className="grid grid-cols-3 gap-2">
                {images.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={getImageUrl(image)}
                      alt={`Image ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nouvelles images */}
          {newImages.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nouvelles images
              </label>
              <div className="grid grid-cols-3 gap-2">
                {newImages.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={getImageUrl(image)}
                      alt={`Nouvelle image ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeNewImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload de nouvelles images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ajouter des images ({images.length + newImages.length}/5)
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleImageUpload(Array.from(e.target.files))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={images.length + newImages.length >= 5}
            />
            <p className="text-sm text-gray-500 mt-1">
              Formats supportés: JPG, PNG, WebP. Maximum 5 images.
            </p>
          </div>

          {/* Boutons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Composant pour afficher une annonce utilisateur avec options de gestion
const UserListingCard = ({ listing, onDelete, onEdit }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // Fermer le menu quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActions && !event.target.closest('.actions-menu')) {
        setShowActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActions]);

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ? Cette action est irréversible.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await apiService.deleteListing(listing.id);
      if (result.success) {
        onDelete(listing.id);
        alert('Annonce supprimée avec succès');
      } else {
        alert('Erreur lors de la suppression: ' + result.message);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de l\'annonce');
    } finally {
      setIsDeleting(false);
    }
  };

  const getImageUrl = (listing) => {
    if (listing.images && listing.images.length > 0) {
      const firstImage = listing.images[0];
      if (typeof firstImage === 'string') {
        return firstImage;
      } else if (firstImage && typeof firstImage === 'object') {
        return firstImage.secure_url || firstImage.url || firstImage.original || '';
      }
    }
    return 'https://via.placeholder.com/300x200?text=No+Image';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', text: 'Actif' },
      sold: { color: 'bg-gray-100 text-gray-800', text: 'Vendu' },
      inactive: { color: 'bg-red-100 text-red-800', text: 'Inactif' },
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'En attente' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="relative">
        <img 
          src={getImageUrl(listing)} 
          alt={listing.title}
          className="w-full h-32 object-cover"
        />
        
        {/* Status Badge */}
        <div className="absolute top-2 left-2">
          {getStatusBadge(listing.status)}
        </div>

        {/* Actions Menu */}
        <div className="absolute top-2 right-2 actions-menu">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-1 bg-white rounded-full shadow-lg hover:bg-gray-50"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" />
            </svg>
          </button>
          
          {showActions && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-10">
              <button
                onClick={() => {
                  setShowActions(false);
                  onEdit(listing);
                }}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Modifier
              </button>
              <button
                onClick={() => {
                  setShowActions(false);
                  handleDelete();
                }}
                disabled={isDeleting}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Suppression...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Supprimer
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="p-3">
        <h3 className="font-medium text-gray-900 mb-1 truncate">{listing.title}</h3>
        <p className="text-lg font-bold text-blue-600 mb-2">€{listing.price}</p>
        
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{listing.views_count || 0} vues</span>
          <span>{listing.favorites_count || 0} favoris</span>
        </div>
        
        <div className="mt-2 text-xs text-gray-400">
          Créé le {new Date(listing.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};
